import { Engine, Scene } from '@babylonjs/core';
import { OrganizedSystemOperations, SystemOperationDefinition } from '@modules/core/src/injectable';
import { FrameTimeoutError } from '@modules/core/src/utils/FrameTimeout';

export class SystemManager {
  private isViewMatrixChanged = false;

  constructor(
    private readonly engine: Engine,
    private readonly scene: Scene,
    private readonly systemOperations: OrganizedSystemOperations
  ) {
    const { unspecific, viewMatrixChanged, viewMatrixChangedEnd, viewMatrixIdle, beforeRender, afterRender } = this.systemOperations;

    if (viewMatrixChanged || viewMatrixChangedEnd) {
      let viewMatrixChangeTimeout: number | null = null;
      const DEBOUNCE_DELAY = 150;
      let lastIdleUpdateTime = 0;
      const IDLE_UPDATE_INTERVAL = 1000; // 1 second interval

      const handleViewMatrixChange = () => {
        if (viewMatrixChanged) {
          this.runViewMatrixChanged();
        }

        if (viewMatrixChangedEnd) {
          if (viewMatrixChangeTimeout !== null) {
            window.clearTimeout(viewMatrixChangeTimeout);
          }

          viewMatrixChangeTimeout = window.setTimeout(() => {
            this.run(viewMatrixChangedEnd);
            viewMatrixChangeTimeout = null;
          }, DEBOUNCE_DELAY);
        }
      };

      let onViewMatrixChangedObserver = this.scene.activeCamera?.onViewMatrixChangedObservable.add(handleViewMatrixChange);

      this.scene.onActiveCameraChanged.add((s) => {
        onViewMatrixChangedObserver?.remove();
        onViewMatrixChangedObserver = s.activeCamera?.onViewMatrixChangedObservable.add(handleViewMatrixChange);
      });

      this.scene.onDisposeObservable.add(() => {
        if (viewMatrixChangeTimeout !== null) {
          window.clearTimeout(viewMatrixChangeTimeout);
        }
      });

      if (viewMatrixChanged) {
        this.scene.onBeforeRenderObservable.add(() => {
          if (this.isViewMatrixChanged) {
            this.run(viewMatrixChanged);
            this.isViewMatrixChanged = false;
          } else {
            if (viewMatrixIdle) {
              const currentTime = Date.now();
              if (currentTime - lastIdleUpdateTime >= IDLE_UPDATE_INTERVAL) {
                this.run(viewMatrixIdle);
                lastIdleUpdateTime = currentTime;
              }
            }
          }
        });
      }
    }

    if (unspecific) {
      this.scene.onBeforeRenderObservable.add(() => {
        this.run(unspecific);
      });
    }

    if (beforeRender) {
      this.scene.onBeforeRenderObservable.add(() => {
        this.run(beforeRender);
      });
    }
    if (afterRender) {
      this.scene.onAfterRenderObservable.add(() => {
        this.run(afterRender);
      });
    }
  }

  private runViewMatrixChanged() {
    this.isViewMatrixChanged = true;
  }

  private run(operations: SystemOperationDefinition[]) {
    const hasMultipleViews = this.engine.activeView && this.engine.views?.length > 1;
    const isInputElement = this.engine.inputElement && this.engine.activeView?.target === (this.engine.inputElement as any);
    const isFirstView = this.engine.activeView === this.engine.views?.[0];

    if (hasMultipleViews && (!isInputElement || !isFirstView)) {
      return;
    }

    for (const op of operations) {
      try {
        op.run();
      } catch (e) {
        if (e instanceof FrameTimeoutError) {
          continue;
        }
        throw e;
      }
    }
  }
}
